import React, { useRef, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import useResizeObserver from 'use-resize-observer';
var supportsPassive = false;
try {
  var opts = /*#__PURE__*/
  Object.defineProperty({}, 'passive', {
    get: function get() {
      supportsPassive = true;
      return true;
    }
  });
  var noop = function noop() {
    return null;
  };
  window.addEventListener('testPassive', noop, opts);
  window.removeEventListener('testPassive', noop, opts);
} catch (e) {}
var supportsPassive$1 = supportsPassive;
function stylesForItem(_ref) {
  var index = _ref.index,
    cache = _ref.cache,
    row = _ref.row,
    column = _ref.column,
    columns = _ref.columns,
    itemHeight = _ref.itemHeight;
  if (itemHeight) {
    cache[index] = cache[index] || {
      position: 'absolute',
      left: column * (100 / columns) + "%",
      top: row * itemHeight + "px",
      width: 100 / columns + '%',
      height: itemHeight + "px"
    };
  } else {
    cache[index] = {
      width: 100 / columns + '%'
    };
  }
  return cache[index];
}
var lastKnownHeights = {};
function SnappyReactGrid(_ref2) {
  var _ref2$id = _ref2.id,
    id = _ref2$id === void 0 ? 'default' : _ref2$id,
    items = _ref2.items,
    RenderComponent = _ref2.component,
    _ref2$defaultVisible = _ref2.defaultVisible,
    defaultVisible = _ref2$defaultVisible === void 0 ? 16 : _ref2$defaultVisible,
    _ref2$defaultOffset = _ref2.defaultOffset,
    defaultOffset = _ref2$defaultOffset === void 0 ? 0 : _ref2$defaultOffset,
    _ref2$columns = _ref2.columns,
    columns = _ref2$columns === void 0 ? 4 : _ref2$columns,
    _ref2$overscanRows = _ref2.overscanRows,
    overscanRows = _ref2$overscanRows === void 0 ? 3 : _ref2$overscanRows,
    className = _ref2.className,
    onScroll = _ref2.onScroll;
  var containerRef = useRef(null);
  var itemRef = useRef(null);
  var _useState = useState(defaultVisible),
    itemsVisible = _useState[0],
    setItemsVisible = _useState[1];
  var _useState2 = useState(defaultOffset),
    itemsOffset = _useState2[0],
    setItemsOffset = _useState2[1];
  var _useResizeObserver = useResizeObserver({
      ref: containerRef
    }),
    containerWidth = _useResizeObserver.width,
    containerHeight = _useResizeObserver.height;
  var _useResizeObserver2 = useResizeObserver({
      ref: itemRef
    }),
    currentItemHeight = _useResizeObserver2.height;
  if (currentItemHeight > 1) lastKnownHeights[id] = currentItemHeight;
  var itemHeight = lastKnownHeights[id] || null;
  var styleCache = useRef({});
  var updateItemVisibility = useCallback(function () {
    if (itemHeight && containerWidth && containerHeight && containerRef.current) {
      var fullHeight = window.innerHeight;
      var _containerRef$current = containerRef.current.getBoundingClientRect(),
        top = _containerRef$current.top,
        bottom = _containerRef$current.bottom;
      var firstVisibleRow = Math.floor(Math.max(-top, 0) / itemHeight);
      var offsetRows = Math.max(0, firstVisibleRow - overscanRows);
      var overscanRowsBefore = Math.min(overscanRows, firstVisibleRow);
      var rowsInView = Math.ceil((fullHeight - Math.max(top, 0) + Math.min(bottom - fullHeight, 0)) / itemHeight);
      var rowsToRender = rowsInView + overscanRowsBefore + overscanRows;
      var _itemsVisible = rowsToRender * columns;
      setItemsVisible(_itemsVisible);
      setItemsOffset(offsetRows * columns);
      var firstVisibleIndex = Math.min(firstVisibleRow * columns, items.length - 1);
      var lastVisibleIndex = Math.min(firstVisibleIndex + rowsInView * columns, items.length - 1);
      return {
        firstVisibleIndex: firstVisibleIndex,
        lastVisibleIndex: lastVisibleIndex
      };
    } else {
      return {
        firstVisibleIndex: null,
        lastVisibleIndex: null
      };
    }
  }, [itemHeight, containerWidth, containerHeight, columns, overscanRows, items.length]);
  var handleScroll = useCallback(function () {
    var _updateItemVisibility = updateItemVisibility(),
      firstVisibleIndex = _updateItemVisibility.firstVisibleIndex,
      lastVisibleIndex = _updateItemVisibility.lastVisibleIndex;
    if (onScroll) {
      onScroll({
        firstVisibleIndex: firstVisibleIndex,
        lastVisibleIndex: lastVisibleIndex
      });
    }
  }, [updateItemVisibility, onScroll]);
  useLayoutEffect(function () {
    styleCache.current = {};
    updateItemVisibility();
    var options = supportsPassive$1 ? {
      passive: true
    } : false;
    window.addEventListener('scroll', handleScroll, options);
    return function () {
      return window.removeEventListener('scroll', handleScroll, options);
    };
  }, [updateItemVisibility, handleScroll]);
  var itemsToRender = useMemo(function () {
    var out = [];
    var endIndex = Math.min(itemsOffset + itemsVisible, items.length);
    for (var index = itemsOffset; index < endIndex; index++) {
      var item = items[index];
      var row = Math.floor(index / columns);
      var column = index % columns;
      out.push(React.createElement(RenderComponent, {
        key: index,
        item: item,
        style: stylesForItem({
          index: index,
          cache: styleCache.current,
          row: row,
          column: column,
          columns: columns,
          itemHeight: itemHeight
        }),
        innerRef: index === itemsOffset ? itemRef : null
      }));
    }
    return out;
  }, [items, itemsOffset, itemsVisible, itemHeight, columns]);
  var containerStyle = itemHeight === null ? {
    position: 'relative'
  } : {
    position: 'relative',
    height: Math.ceil(items.length / columns) * itemHeight
  };
  return React.createElement("div", {
    className: className,
    style: containerStyle,
    ref: containerRef
  }, itemsToRender);
}
export default SnappyReactGrid;
export { SnappyReactGrid };
//# sourceMappingURL=snappy-react-grid.esm.js.map